import React, { useState, useEffect } from 'react';
import { useProjectStore, usePoleStore } from '../../stores';
import { Pole } from '../../types';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';
import './OLTPortConfigDialog.css';

interface OLTPortConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pole: Pole;
}

interface PortConfig {
  port_number: number;
  power_dbm: number;
}

export const OLTPortConfigDialog: React.FC<OLTPortConfigDialogProps> = ({ isOpen, onClose, pole }) => {
  const { currentProject } = useProjectStore();
  const { updatePole, fetchPoles } = usePoleStore();

  const [portConfigs, setPortConfigs] = useState<PortConfig[]>([]);
  const [portCount, setPortCount] = useState(pole.olt_port_count || 1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPortCount(pole.olt_port_count || 1);

    if (pole.olt_port_count) {
      // Initialize port configs
      const configs: PortConfig[] = [];
      const existingConfigs = pole.cassette?.olt_port_configs || [];

      for (let i = 1; i <= pole.olt_port_count; i++) {
        const existing = existingConfigs.find((c: any) => c.port_number === i);
        configs.push(existing || {
          port_number: i,
          power_dbm: 3.0, // Default OLT output power
        });
      }

      setPortConfigs(configs);
    }
  }, [pole]);

  const handlePortChange = (portNumber: number, field: string, value: any) => {
    setPortConfigs(prev => prev.map(p =>
      p.port_number === portNumber ? { ...p, [field]: value } : p
    ));
  };

  const handleAddPort = () => {
    const newPortNumber = portCount + 1;
    setPortCount(newPortNumber);
    setPortConfigs(prev => [
      ...prev,
      {
        port_number: newPortNumber,
        power_dbm: 3.0,
      }
    ]);
  };

  const handleRemovePort = () => {
    if (portCount > 1) {
      const newPortCount = portCount - 1;
      setPortCount(newPortCount);
      setPortConfigs(prev => prev.filter(p => p.port_number <= newPortCount));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!currentProject) {
        throw new Error('Nie je vybraný žiadny projekt');
      }

      const updateData = {
        olt_port_count: portCount,
        cassette: {
          ...pole.cassette,
          olt_port_configs: portConfigs,
        },
      };

      console.log('[OLTPortConfig] Updating pole:', pole.id);
      console.log('[OLTPortConfig] Update data:', JSON.stringify(updateData, null, 2));

      await updatePole(currentProject.id, pole.id, updateData);

      console.log('[OLTPortConfig] Update successful');
      await fetchPoles(currentProject.id);
      onClose();
    } catch (err: any) {
      console.error('[OLTPortConfig] Update failed:', err);
      setError(getErrorMessage(err, 'Nepodarilo sa uložiť konfiguráciu OLT portov.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog olt-port-config-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>🔌 OLT Port Configuration</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-content">
          {error && <div className="error-message">{error}</div>}

          <div className="port-config-intro">
            <p>
              Konfigurácia portov OLT. Každý port má výkon (dBm). Splitters sa konfigurujú v Cassette Editor.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
              <strong>Počet portov:</strong>
              <button
                type="button"
                onClick={handleRemovePort}
                disabled={portCount <= 1}
                style={{
                  background: portCount <= 1 ? '#ccc' : '#f44336',
                  border: 'none',
                  color: 'white',
                  cursor: portCount <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  padding: '4px 10px',
                  borderRadius: '4px',
                }}
                title="Odobrať port"
              >
                −
              </button>
              <span style={{ fontSize: '18px', fontWeight: 'bold', minWidth: '40px', textAlign: 'center' }}>
                {portCount}
              </span>
              <button
                type="button"
                onClick={handleAddPort}
                disabled={portCount >= 64}
                style={{
                  background: portCount >= 64 ? '#ccc' : '#4caf50',
                  border: 'none',
                  color: 'white',
                  cursor: portCount >= 64 ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  padding: '4px 10px',
                  borderRadius: '4px',
                }}
                title="Pridať port"
              >
                +
              </button>
            </div>
          </div>

          <div className="port-configs-list">
            {portConfigs.map((config) => (
              <div key={config.port_number} className="port-config-item">
                <div className="port-header">
                  <h4>Port {config.port_number}</h4>
                </div>

                <div className="port-fields">
                  <div className="form-group">
                    <label>Output Power (dBm)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={config.power_dbm}
                      onChange={(e) => handlePortChange(config.port_number, 'power_dbm', parseFloat(e.target.value))}
                    />
                    <small>Typical: 2-5 dBm</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Zrušiť
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

